import { isEmpty, isNil } from 'lodash';
import { CSSProperties, ReactNode } from 'react';
import { View } from '@shared/components/View';
import { buildCardField } from '@shared/data/cards/CardField';
import { isDeletedEntity } from '@shared/data/entity/Entity';
import { AddressField } from '@shared/data/entity/fields/AddressField';
import { DateField } from '@shared/data/entity/fields/DateField';
import { EmailField } from '@shared/data/entity/fields/EmailField';
import { Field } from '@shared/data/entity/fields/Field';
import { NumberField } from '@shared/data/entity/fields/NumberField';
import { RelatedEntityField } from '@shared/data/entity/fields/RelatedEntityField';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TextField } from '@shared/data/entity/fields/TextField';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { WarningSubfooter } from '@shared/scenes/entity/components/WarningSubfooter';
import { AddressEditField } from '@shared/scenes/entity/components/fields/AddressEditField';
import { DateEditField } from '@shared/scenes/entity/components/fields/DateEditField/DateEditField';
import { EditFieldContainerProps } from '@shared/scenes/entity/components/fields/EditFieldContainerProps';
import { EmailEditField } from '@shared/scenes/entity/components/fields/EmailEditField';
import { NumberEditField } from '@shared/scenes/entity/components/fields/NumberEditField';
import { TagEditField } from '@shared/scenes/entity/components/fields/TagEditField/TagEditField';
import { TextEditField } from '@shared/scenes/entity/components/fields/TextEditField';
import { useWorkflowEntitiesByIds } from '@shared/scenes/workflows/hooks';
import {
  getComponentLabel,
  isAddressComponent,
  isDateComponent,
  isEmailComponent,
  isNumberComponent,
  isTagComponent,
  isTextComponent,
} from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { FieldRecord } from '../../types';

interface Props {
  field: Field | null;
  fields: FieldRecord;
  workflowTemplateId: string;
  component: API.RelatedCardLookupComponent;
  error?: string;
  style?: CSSProperties;
}

type EntityAndField = { field: Field; entity: API.WorkflowEntity };

export function RelatedCardLookupEditField({
  fields,
  component,
  error,
  workflowTemplateId,
  style,
}: Props) {
  // Get related entity ids from the reference field
  const relatedField = fields[component.relatedCardComponentId] as RelatedEntityField | undefined;
  const relatedIds = relatedField?.items.map(i => i.id) ?? [];

  // Entities already present in refs (loaded with the card)
  const entitiesFromRefs = relatedField?.relatedEntities ?? [];

  const missingIds = relatedIds.filter(id => !entitiesFromRefs.some(e => e.id === id));

  // Always query with at least one id so we receive workflowTemplate refs (for create)
  const queryIds = missingIds.length > 0 ? missingIds : relatedIds.slice(0, 1);

  const { workflowTemplate: fetchedWorkflowTemplate, workflowEntities: fetched } =
    useWorkflowEntitiesByIds({
      workflowTemplateId: component.lookupWorkflowTemplateId,
      workflowEntityIds: queryIds,
    });

  const workflowTemplate =
    relatedField?.workflowTemplateRefs?.find(t => t.id === component.lookupWorkflowTemplateId) ??
    fetchedWorkflowTemplate;

  const allEntities: API.WorkflowEntity[] = relatedIds
    .map(id => [...entitiesFromRefs, ...fetched].find(e => e.id === id))
    .filter(notNil);

  if (isEmpty(allEntities)) {
    return null;
  }

  const lookupComponent = workflowTemplate?.components.find(
    c => c.id === component.lookupComponentId,
  );

  if (isNil(lookupComponent)) {
    return null;
  }

  const lookupFields: EntityAndField[] = allEntities
    .map(entity => {
      const field = buildCardField({
        entity,
        component: lookupComponent,
        refs: {
          workflowEntityRefs: null,
          workflowTemplateRefs: null,
          personRefs: null,
          recurringEntitiesScheduleRefs: null,
        },
      });
      return { field, entity };
    })
    .filter((item): item is EntityAndField => notNil(item.field));

  if (isEmpty(lookupFields)) {
    return null;
  }

  return (
    <View>
      {lookupFields.map((item, index) => (
        <LookupField
          key={index}
          field={item.field}
          entity={item.entity}
          isFirst={index === 0}
          isLast={index === lookupFields.length - 1}
          lookupParentComponent={component}
          lookupComponent={lookupComponent}
          error={error}
          workflowTemplateId={workflowTemplateId}
          style={style}
        />
      ))}
    </View>
  );
}

interface LookupFieldProps {
  field: Field;
  entity: API.WorkflowEntity;
  isFirst: boolean;
  isLast: boolean;
  lookupParentComponent: API.RelatedCardLookupComponent;
  lookupComponent: API.AnyComponent;
  error: string | undefined;
  workflowTemplateId: string;
  style?: CSSProperties;
  subfooter?: ReactNode;
  isGrayScale?: boolean;
  isItalic?: boolean;
}

type LookupEditFieldContainerProps = Pick<
  EditFieldContainerProps,
  | 'error'
  | 'showLabel'
  | 'label'
  | 'indentLabel'
  | 'showIcon'
  | 'reserveIconSpace'
  | 'compactTop'
  | 'compactBottom'
  | 'subfooter'
  | 'isGrayScale'
  | 'isItalic'
  | 'style'
>;

function LookupField({
  field,
  entity,
  workflowTemplateId,
  isFirst,
  isLast,
  lookupParentComponent,
  lookupComponent,
  error,
  style,
}: LookupFieldProps) {
  const responsiveForms = useResponsiveFormContext();
  const isDeleted = isDeletedEntity(entity);
  const reserveIconSpace = !isFirst && !(responsiveForms && field instanceof AddressField);

  const commonProps: LookupEditFieldContainerProps = {
    error,
    showLabel: isFirst,
    label: getComponentLabel(lookupParentComponent),
    indentLabel: false,
    showIcon: getShowIcon({ isFirst, responsiveForms, field }),
    reserveIconSpace,
    compactTop: !isFirst,
    compactBottom: !isLast,
    subfooter: (
      <WarningSubfooter
        showWarning={isDeleted}
        message={i18n('workflowTemplate.relatedCardDeleted')}
      />
    ),
    isGrayScale: isDeleted,
    isItalic: isDeleted,
    style,
  };

  if (isAddressComponent(lookupComponent) && field instanceof AddressField) {
    return <AddressEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isTextComponent(lookupComponent) && field instanceof TextField) {
    return <TextEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isNumberComponent(lookupComponent) && field instanceof NumberField) {
    return <NumberEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isEmailComponent(lookupComponent) && field instanceof EmailField) {
    return <EmailEditField field={field} component={lookupComponent} {...commonProps} />;
  }
  if (isDateComponent(lookupComponent) && field instanceof DateField) {
    return (
      <DateEditField
        field={field}
        component={lookupComponent}
        workflowTemplateId={workflowTemplateId}
        {...commonProps}
      />
    );
  }
  if (isTagComponent(lookupComponent) && field instanceof TagField) {
    return (
      <TagEditField
        field={field}
        component={lookupComponent}
        workflowTemplateId={workflowTemplateId}
        {...commonProps}
      />
    );
  }

  return null;
}

function getShowIcon({
  isFirst,
  responsiveForms,
  field,
}: {
  isFirst: boolean;
  responsiveForms: boolean;
  field: Field;
}) {
  // For address, only show the icon (and thus indent) when NOT responsive and on first item
  if (field instanceof AddressField) {
    return !responsiveForms && isFirst;
  } else {
    return isFirst || responsiveForms;
  }
}
